import api from "./api";
import type {
  AdminCaseDetail,
  AdminCaseListResponse,
  ChecklistItem,
  ChecklistTemplate,
  HandoverFile,
  HandoverItem,
  OffboardingCaseDetail,
  ReceivingCaseDetail,
  ReceivingCaseItem,
} from "@/types/offboarding";

export interface MyCasePayload {
  reason_category: string;
  reason_detail?: string | null;
  desired_last_working_date?: string | null;
}

export interface AdminCaseCreatePayload {
  target_user_id: string;
  reason_category: string;
  reason_detail?: string | null;
  final_resignation_date: string;
  handover_receiver_id?: string | null;
}

export interface AdminCaseListParams {
  status?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

class OffboardingService {
  private base = "/offboarding";

  // ----- 직원: 사직서 -----
  async getMyCase(): Promise<{ case: OffboardingCaseDetail | null }> {
    const r = await api.get(`${this.base}/my/case`);
    return r.data;
  }
  async createMyCase(data: MyCasePayload): Promise<{ case: OffboardingCaseDetail }> {
    const r = await api.post(`${this.base}/my/case`, data);
    return r.data;
  }
  async updateMyCase(data: MyCasePayload): Promise<{ case: OffboardingCaseDetail }> {
    const r = await api.put(`${this.base}/my/case`, data);
    return r.data;
  }
  // 전자서명 제출: signature_image(캔버스 data URL) + password(본인 확인) — 미전달 시 단순 기록 제출
  async submitMyCase(data?: { signature_image: string; password: string }): Promise<{ case: OffboardingCaseDetail }> {
    const r = await api.post(`${this.base}/my/case/submit`, data);
    return r.data;
  }
  async withdrawMyCase(): Promise<{ success: boolean }> {
    const r = await api.post(`${this.base}/my/case/withdraw`);
    return r.data;
  }
  async getMyLetterHtml(): Promise<string> {
    const r = await api.get(`${this.base}/my/case/letter`, { responseType: "text" });
    return r.data;
  }
  async getMyActionCount(): Promise<{ count: number }> {
    const r = await api.get(`${this.base}/my/action-count`);
    return r.data;
  }
  // 프로필 '퇴사 신청' 진입점 노출 여부 (회사 설정)
  async getMyConfig(): Promise<{ allow_self_service: boolean }> {
    const r = await api.get(`${this.base}/my/config`);
    return r.data;
  }

  // ----- 직원: 인수인계 작성 -----
  async createHandoverItem(data: { title: string; description?: string | null }): Promise<HandoverItem> {
    const r = await api.post(`${this.base}/my/case/handover-items`, data);
    return r.data;
  }
  async updateHandoverItem(
    itemId: number,
    data: { title?: string; description?: string | null }
  ): Promise<HandoverItem> {
    const r = await api.put(`${this.base}/my/handover-items/${itemId}`, data);
    return r.data;
  }
  async deleteHandoverItem(itemId: number): Promise<{ success: boolean }> {
    const r = await api.delete(`${this.base}/my/handover-items/${itemId}`);
    return r.data;
  }
  async uploadHandoverFile(itemId: number, file: File): Promise<HandoverFile> {
    const formData = new FormData();
    formData.append("file", file);
    const r = await api.post(`${this.base}/my/handover-items/${itemId}/files`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data;
  }
  async deleteHandoverFile(itemId: number, fileId: number): Promise<{ success: boolean }> {
    const r = await api.delete(`${this.base}/my/handover-items/${itemId}/files/${fileId}`);
    return r.data;
  }
  async submitHandoverItem(itemId: number): Promise<HandoverItem> {
    const r = await api.post(`${this.base}/my/handover-items/${itemId}/submit`);
    return r.data;
  }
  async downloadHandoverFile(itemId: number, fileId: number, fileName: string): Promise<void> {
    const r = await api.get(
      `${this.base}/handover-items/${itemId}/files/${fileId}/download`,
      { responseType: "blob" }
    );
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  // ----- 인수자 -----
  async listReceivingCases(): Promise<{ items: ReceivingCaseItem[] }> {
    const r = await api.get(`${this.base}/receiving`);
    return r.data;
  }
  async getReceivingCase(caseId: string): Promise<ReceivingCaseDetail> {
    const r = await api.get(`${this.base}/receiving/${caseId}`);
    return r.data;
  }
  async confirmHandoverItem(itemId: number): Promise<{ success: boolean; all_confirmed: boolean }> {
    const r = await api.post(`${this.base}/handover-items/${itemId}/confirm`);
    return r.data;
  }
  async returnHandoverItem(itemId: number, reason: string): Promise<HandoverItem> {
    const r = await api.post(`${this.base}/handover-items/${itemId}/return`, { reason });
    return r.data;
  }

  // ----- 관리자: 케이스 -----
  async listAdminCases(params?: AdminCaseListParams): Promise<AdminCaseListResponse> {
    const r = await api.get(`${this.base}/admin/cases`, { params });
    return r.data;
  }
  async getAdminCase(caseId: string): Promise<AdminCaseDetail> {
    const r = await api.get(`${this.base}/admin/cases/${caseId}`);
    return r.data;
  }
  async getAdminLetterHtml(caseId: string): Promise<string> {
    const r = await api.get(`${this.base}/admin/cases/${caseId}/letter`, { responseType: "text" });
    return r.data;
  }
  async createAdminCase(
    data: AdminCaseCreatePayload
  ): Promise<{ case: OffboardingCaseDetail; warnings: string[] }> {
    const r = await api.post(`${this.base}/admin/cases`, data);
    return r.data;
  }
  async approveCase(
    caseId: string,
    data: { final_resignation_date: string; handover_receiver_id?: string | null }
  ): Promise<{ case: OffboardingCaseDetail; warnings: string[] }> {
    const r = await api.post(`${this.base}/admin/cases/${caseId}/approve`, data);
    return r.data;
  }
  async rejectCase(caseId: string, reason: string): Promise<{ success: boolean }> {
    const r = await api.post(`${this.base}/admin/cases/${caseId}/reject`, { reason });
    return r.data;
  }
  async updateAdminCase(
    caseId: string,
    // 백엔드는 null을 "해제"로 처리하지 않으므로 값 변경만 허용 (미변경 필드는 생략)
    data: { final_resignation_date?: string; handover_receiver_id?: string }
  ): Promise<{ case: OffboardingCaseDetail; warnings: string[] }> {
    const r = await api.put(`${this.base}/admin/cases/${caseId}`, data);
    return r.data;
  }
  async cancelCase(caseId: string, reason: string): Promise<{ success: boolean }> {
    const r = await api.post(`${this.base}/admin/cases/${caseId}/cancel`, { reason });
    return r.data;
  }
  async finalizeCase(
    caseId: string,
    data: { resignation_date?: string | null; force?: boolean; force_reason?: string | null }
  ): Promise<AdminCaseDetail> {
    const r = await api.post(`${this.base}/admin/cases/${caseId}/finalize`, data);
    return r.data;
  }
  async getAdminPendingCount(): Promise<{ count: number }> {
    const r = await api.get(`${this.base}/admin/pending-count`);
    return r.data;
  }

  // ----- 관리자: 회사별 설정 -----
  async getAdminSettings(): Promise<{ allow_self_service: boolean }> {
    const r = await api.get(`${this.base}/admin/settings`);
    return r.data;
  }
  async updateAdminSettings(data: { allow_self_service: boolean }): Promise<{ allow_self_service: boolean }> {
    const r = await api.put(`${this.base}/admin/settings`, data);
    return r.data;
  }
  // 오프라인(종이) 사직서 스캔본 업로드 — 생성/전자서명 사직서를 대체
  async uploadResignationScan(
    caseId: string,
    file: File
  ): Promise<{ success: boolean; resignation_document_id: number }> {
    const formData = new FormData();
    formData.append("file", file);
    const r = await api.post(`${this.base}/admin/cases/${caseId}/resignation-scan`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data;
  }

  // ----- 관리자: 체크리스트 인스턴스 -----
  async patchChecklistItem(
    itemId: number,
    data: { status?: string; note?: string | null }
  ): Promise<ChecklistItem> {
    const r = await api.patch(`${this.base}/admin/checklist-items/${itemId}`, data);
    return r.data;
  }
  async addChecklistItem(
    caseId: string,
    data: { name: string; description?: string | null; category?: string; is_required?: boolean; timing?: string }
  ): Promise<ChecklistItem> {
    const r = await api.post(`${this.base}/admin/cases/${caseId}/checklist-items`, data);
    return r.data;
  }
  async deleteChecklistItem(itemId: number): Promise<{ success: boolean }> {
    const r = await api.delete(`${this.base}/admin/checklist-items/${itemId}`);
    return r.data;
  }

  // ----- 관리자: 템플릿 (퇴사 메뉴얼) -----
  async listTemplates(): Promise<{ items: ChecklistTemplate[] }> {
    const r = await api.get(`${this.base}/templates`);
    return r.data;
  }
  async createTemplate(data: Partial<ChecklistTemplate>): Promise<ChecklistTemplate> {
    const r = await api.post(`${this.base}/templates`, data);
    return r.data;
  }
  async updateTemplate(id: number, data: Partial<ChecklistTemplate>): Promise<ChecklistTemplate> {
    const r = await api.put(`${this.base}/templates/${id}`, data);
    return r.data;
  }
  async deleteTemplate(id: number): Promise<{ success: boolean }> {
    const r = await api.delete(`${this.base}/templates/${id}`);
    return r.data;
  }
  async reorderTemplates(orderedIds: number[]): Promise<{ success: boolean }> {
    const r = await api.put(`${this.base}/templates/reorder`, { ordered_ids: orderedIds });
    return r.data;
  }
}

export const offboardingService = new OffboardingService();
export default offboardingService;
